import styled from "styled-components";
import { Rgb } from "./types";
import { BaseComponentProps } from "../types";
import "../../designTokens/variables.css";
import {
  LARGE_SWATCH_WIDTH_IN_PX,
  SWATCH_WIDTH_IN_PX,
  TestIds,
} from "./constants";
import { getBackgroundColor } from "./utils/getBackgroundColor";

type StyledSwatchProps = {
  hex?: string;
  rgb?: Rgb;
};

const StyledSwatch = styled.div<StyledSwatchProps>`
  cursor: pointer;
  background-color: ${(props) => getBackgroundColor(props)};
  border-radius: var(--size-border-radius-large);
  border: 1px solid var(--color-primary-200);
`;

type StyledImageSwatchProps = {
  imageUrl: string;
};

const StyledImageSwatch = styled(StyledSwatch)<StyledImageSwatchProps>`
  background-image: url("${(props) => props.imageUrl}");
  background-repeat: round;
`;

const StyledEmptySwatch = styled(StyledSwatch)`
  cursor: not-allowed;
  background-color: var(--color-neutral-000-full);
  overflow: hidden;
  position: relative;
  ::after {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    margin-left: -100%;
    height: 1px;
    width: 200%;
    background-color: var(--color-primary-200);
    transform-origin: center;
    transform: rotateZ(-45deg);
  }
`;

type SwatchContainerProps = {
  sideLengthInPx: number;
  selected?: boolean;
};

const SwatchContainer = styled.div<SwatchContainerProps>`
  height: ${(props) => props.sideLengthInPx + 2}px;
  width: ${(props) => props.sideLengthInPx + 2}px;
  padding: 1px;

  position: relative;
  ::before {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    content: "";
    z-index: 0;
    border: 1px solid var(--color-primary-400);
    border-radius: var(--size-border-radius-large);
    opacity: ${(props) => (props.selected ? 1 : 0)};
    transition: opacity ease-in-out 150ms;
  }
  > ${StyledSwatch}, > ${StyledImageSwatch} {
    position: relative;
    height: ${(props) => props.sideLengthInPx}px;
    width: ${(props) => props.sideLengthInPx}px;
  }
`;

type SwatchComponentProps = Omit<
  BaseSwatchProps,
  "sideLengthInPx" | "selected" | "testId"
>;

const SwatchComponent = ({
  hex,
  imageUrl,
  rgb,
  ...props
}: SwatchComponentProps) => {
  if (imageUrl) {
    return <StyledImageSwatch imageUrl={imageUrl} {...props} />;
  }
  if (rgb || hex) {
    return <StyledSwatch hex={hex} rgb={rgb} {...props} />;
  }
  return (
    <StyledEmptySwatch {...props} data-testid={TestIds.EmptySwatchInner} />
  );
};

type BaseSwatchProps = SwatchProps &
  Pick<SwatchContainerProps, "sideLengthInPx">;

const BaseSwatch = ({
  hex,
  imageUrl,
  rgb,
  sideLengthInPx,
  selected,
  testId,
  ...props
}: BaseSwatchProps) => {
  return (
    <SwatchContainer sideLengthInPx={sideLengthInPx} selected={selected}>
      <SwatchComponent
        data-testid={testId}
        hex={hex}
        imageUrl={imageUrl}
        rgb={rgb}
        {...props}
      />
    </SwatchContainer>
  );
};

type SwatchProps = BaseComponentProps<
  "div",
  {
    hex?: string;
    imageUrl?: string;
    rgb?: Rgb;
    selected?: boolean;
    testId?: string;
  }
>;

export const Swatch = ({
  selected = false,
  testId = TestIds.Swatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const LargeSwatch = ({
  selected = false,
  testId = TestIds.LargeSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={LARGE_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const EmptySwatch = ({
  selected = false,
  testId = TestIds.EmptySwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};

export const EmptyLargeSwatch = ({
  selected = false,
  testId = TestIds.EmptyLargeSwatch,
  ...props
}: SwatchProps) => {
  return (
    <BaseSwatch
      sideLengthInPx={LARGE_SWATCH_WIDTH_IN_PX}
      selected={selected}
      testId={testId}
      {...props}
    />
  );
};
